
import { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useCommunity } from '@/contexts/CommunityContext';

export function useAuthForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentCommunity } = useCommunity();
  const { toast } = useToast();

  const handleSignUp = async (e: React.FormEvent, returnTo: string = '/', customPassword?: string) => {
    e.preventDefault();
    setLoading(true);
    
    // Use custom password if provided, otherwise use the state password
    const passwordToUse = customPassword || password;
    
    // Determine the email redirect URL based on returnTo
    let emailRedirectTo = `${window.location.origin}/complete-registration`;
    
    // If returnTo is something other than root, use it directly
    if (returnTo && returnTo !== '/') {
      emailRedirectTo = `${window.location.origin}${returnTo}`;
    }
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password: passwordToUse,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`,
            community_id: currentCommunity?.id
          },
          emailRedirectTo,
        },
      });
      
      if (error) throw error;
      
      toast({
        title: "Check your email",
        description: "We've sent you a link to verify your email address.",
      });

      return { data, error: null };
    } catch (error: any) {
      console.error("Sign up error:", error);
      
      toast({
        variant: "destructive",
        title: "Error creating account",
        description: error.message || "An error occurred during sign up.",
      });
      
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error: any) {
      console.error("Sign in error:", error);
      
      toast({
        variant: "destructive",
        title: "Error signing in",
        description: error.message || "Incorrect email or password.",
      });
      
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async (returnTo: string = '/') => {
    setLoading(true);
    
    try {
      // Determine redirect URL for Google OAuth
      let redirectTo = `${window.location.origin}/complete-registration`;
      
      // If returnTo is something other than root, use it directly
      if (returnTo && returnTo !== '/') {
        redirectTo = `${window.location.origin}${returnTo}`;
      }
      
      console.log(`[useAuthForm] Google sign in with redirect to: ${redirectTo}`);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            community_id: currentCommunity?.id || ''
          }
        }
      });
      
      if (error) throw error;
      
      return { error: null };
    } catch (error: any) {
      console.error("Google sign in error:", error);
      
      toast({
        variant: "destructive",
        title: "Google sign in failed",
        description: error.message || "An error occurred during Google sign in.",
      });
      
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordReset = async (emailAddress: string, redirectUrl?: string) => {
    setLoading(true);
    
    try {
      const finalRedirectUrl = redirectUrl || `${window.location.origin}/auth/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(emailAddress, {
        redirectTo: finalRedirectUrl
      });
      
      if (error) throw error;
      
      toast({
        title: "Password reset email sent",
        description: "Check your email for a link to reset your password.",
      });
      
      return true;
    } catch (error: any) {
      console.error("Password reset request error:", error);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An error occurred while requesting a password reset.",
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    window.location.href = '/auth/request-reset-password';
  };

  const handleUpdatePassword = async (newPassword: string, token?: string) => {
    setLoading(true);
    
    try {
      console.log("Attempting to update password...");
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        console.error("Password update error:", error);
        throw new Error("Failed to update password. The reset link may have expired. Please request a new one.");
      }
      
      console.log("Password updated successfully");
      
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated. You can now sign in with your new password.",
      });
      
      return true;
    } catch (error: any) {
      console.error("Password update error:", error);
      
      toast({
        variant: "destructive",
        title: "Error updating password",
        description: error.message || "An error occurred while updating your password. Please request a new reset link.",
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Note: Welcome emails are now automatically sent by the database 
  // when users are added to communities via the add_user_to_community function
  const handlePostConfirmation = async (user: any) => {
    // This function is kept for compatibility but welcome emails 
    // are now handled automatically by the database
    console.log('Post-confirmation handler called - welcome emails now handled by database');
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    loading,
    handleSignUp,
    handleSignIn,
    handleGoogleSignIn,
    handleResetPassword,
    handleRequestPasswordReset,
    handleUpdatePassword,
    handlePostConfirmation,
  };
}
